import { defineStore } from "pinia";
import { PushNotifications } from "@capacitor/push-notifications";
import * as Sentry from "@sentry/vue";

import type { PushNotificationsState, PushNotificationOptions } from "./interfaces";
import type { ActionPerformed, PushNotificationSchema, Token } from "@capacitor/push-notifications";
import { StateNamespace } from "@/Modules/App/Models/State/StateNamespace";
import { NotificationType } from "@/Modules/App/Models/Domain/NotificationType";

import PostPushTokenActionHandler from "./ActionHandlers/PostPushToken";
import UpdatePushTokenActionHandler from "./ActionHandlers/UpdatePushToken";
import ShowNotificationActionHandler from "./ActionHandlers/ShowNotification";
import DeletePushTokenActionHandler from "./ActionHandlers/DeletePushToken";
import CheckIsAvailablePushNotificationsActionHandler from "./ActionHandlers/CheckIsAvailable";
import PerformNotificationActionHandler from "./ActionHandlers/PerformNotification";

import useAppStore from "@/Modules/App/Store";

import { isApp } from "@/Utils/Ionic";
import { danger, log } from "@/Utils/Debug";
import { hasValue } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";

const state: PushNotificationsState = {
  isGranted: false,
  isAvailable: false,
  token: undefined,
};

export const usePushNotificationsStore = defineStore(StateNamespace.PUSH_NOTIFICATIONS, {
  state: () => ({ ...state }),
  getters: {},

  actions: {
    async init() {
      if (!isApp()) return;

      this.token = undefined;
      this.isAvailable = false;
      this.isGranted = false;

      try {
        if (await this.updateIsGranted()) {
          this.addListeners();

          await PushNotifications.register().catch(() => {
            danger("Error register Push notifications");
          });
        }
      } catch (e) {
        danger("Error init push notifications", e);

        Sentry.captureException(e);
      }
    },

    async updateIsGranted(val?: boolean): Promise<boolean> {
      if (hasValue(val)) {
        this.isGranted = val;
      } else {
        this.isGranted = await this.hasPermisions();
      }

      return this.isGranted;
    },

    async hasPermisions(): Promise<boolean> {
      if (!isApp()) return false;

      const permissions = await PushNotifications.checkPermissions();

      return permissions.receive === "granted";
    },

    async requestPermissions(): Promise<boolean> {
      if (!isApp()) return false;

      const permissions = await PushNotifications.requestPermissions();

      await this.updateIsGranted();

      if (permissions.receive === "granted") {
        this.init();

        const appStore = useAppStore();

        appStore.showSnackbar({
          message: "settings.notification.update_success",
          type: NotificationType.SUCCESS,
        });

        return true;
      }

      return false;
    },

    async showNotification(options: PushNotificationOptions) {
      const handler = new ShowNotificationActionHandler();

      handler.handle(options);
    },

    async updatePushToken(enabled: boolean): Promise<boolean | string | undefined> {
      if (isValidString(this.token)) {
        const handler = new UpdatePushTokenActionHandler();

        return handler.handle(this.token, enabled);
      } else {
        return this.requestPermissions();
      }
    },

    postPushToken(token: string) {
      const handler = new PostPushTokenActionHandler();

      return handler.handle(token);
    },

    addListeners() {
      if (!isApp()) return;

      PushNotifications.removeAllListeners();

      // On success, we should be able to receive notifications
      PushNotifications.addListener("registration", async (token: Token) => {
        log("Push Token received", token.value);

        const handler = new CheckIsAvailablePushNotificationsActionHandler();
        const isAvaiblable = await handler.handle(token.value);

        if (!hasValue(isAvaiblable)) {
          this.postPushToken(token.value);
        } else {
          this.token = token.value;
        }

        log("Push registration success, token: " + token.value);
      });

      // Some issue with our setup and push will not work
      PushNotifications.addListener("registrationError", (error: unknown) => {
        danger("Error on registration: " + JSON.stringify(error));
        Sentry.captureException("Error push token registration");
      });

      // Show us the notification payload if the app is open on our device
      PushNotifications.addListener("pushNotificationReceived", (notification: PushNotificationSchema) => {
        log("Push received: " + JSON.stringify(notification));
      });

      // Method called when tapping on a notification
      PushNotifications.addListener("pushNotificationActionPerformed", (notification: ActionPerformed) => {
        log("Push action performed: " + JSON.stringify(notification));

        PushNotifications.removeAllDeliveredNotifications();

        const AH = new PerformNotificationActionHandler();

        AH.handle(notification);
      });
    },

    async deleteTokens(): Promise<boolean> {
      if (isValidString(this.token)) {
        const AH = new DeletePushTokenActionHandler();

        return AH.handle(this.token);
      }

      return true;
    },
  },
});

export type PushNotificationStore = ReturnType<typeof usePushNotificationsStore>;

export default usePushNotificationsStore;
